import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { ScanBadgeButton } from '@/components/iem/actions/ScanBadgeButton';
import { ProfileSummary } from '@/components/iem/identity/ProfileSummary';
import { getEnvironmentBanner } from '@/utils/environment';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { useCustomerLookup, useBadgeLookup } from '@/hooks';
import { CustomerProfileSkeleton } from '@/components/iem/identity/CustomerProfileSkeleton';
import { httpClient, getErrorMessage } from '@/utils/httpClient';
import { getEligibilityReason, isCustomerEligibilityError } from '@/utils/iemEligibility';
import { API_CONFIG } from '@/config/api';
import { buildPostgrestOrIlikeFilter } from '@/utils/postgrestFilters';
import { PersonSizingDialog } from '@/components/iem/dialogs/PersonSizingDialog';
import { useNotifications } from '@/stores/useNotificationStore';
import { iemService } from '@/services/iem';
import { entityService } from '@/services/EntityService';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { useAuth } from '@/hooks/useAuth';
import { useMyPersonEntityId } from '@/hooks/useMyPersonEntityId';

interface PersonSuggestion {
  id: string;
  display_name: string;
  rank?: string;
  unit?: string;
  individual_id?: string;
}

type PanelState = 'idle' | 'loading' | 'loaded' | 'error';

interface CustomerIdentityPanelProps {
  lockToCurrentUser?: boolean;
}

export const CustomerIdentityPanel: React.FC<CustomerIdentityPanelProps> = ({
  lockToCurrentUser = false,
}) => {
  const { currentCustomer, setCustomer, startServiceTimer, getPersistedIndividualId } =
    useIemFrontDoorStore();
  const customerLookupMutation = useCustomerLookup();
  const badgeLookupMutation = useBadgeLookup();
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const { hasRole, isDemoServiceMemberSession, user } = useAuth();
  const { myPersonEntityId: rpcPersonEntityId } = useMyPersonEntityId();
  // Service-members may only open the sizing dialog for their own person entity.
  const isServiceMember = hasRole('service-member');
  // Comptroller cannot manage sizing at all (permission matrix).
  const isComptroller = hasRole('comptroller');
  // For demo SM sessions the HS256 token has sub = person entity ID directly;
  // the DB RPC returns null because no users row exists for the demo token.
  // Fall back to user.id which is set to the person entity ID in loginAsDemoServiceMember.
  const myPersonEntityId =
    rpcPersonEntityId ?? (isDemoServiceMemberSession ? (user?.id ?? null) : null);
  const isIdentitySelectionLocked = isServiceMember || lockToCurrentUser;
  const canManageSizingForCurrentCustomer =
    !isComptroller &&
    (!isIdentitySelectionLocked || (!!currentCustomer && currentCustomer.id === myPersonEntityId));

  const { data: peopleRows = [], isLoading: peopleAvailabilityLoading } = useQuery({
    queryKey: ['iem', 'people-availability'],
    queryFn: async () => {
      return await httpClient.get<Array<{ id: string }>>(
        '/api/rest/data/v1/entities?entity_type=eq.person&is_current=eq.true&select=id&limit=1',
      );
    },
    staleTime: 10_000,
    refetchInterval: 10_000,
  });

  const hasPeopleData = peopleRows.length > 0;
  const isScanBlocked = peopleAvailabilityLoading || !hasPeopleData;

  // Start service timer when customer is selected
  useEffect(() => {
    if (currentCustomer) {
      startServiceTimer();
    }
  }, [currentCustomer, startServiceTimer]);

  const [state, setState] = useState<PanelState>('idle');

  const [individualId, setIndividualId] = useState('');
  const [autocompleteValue, setAutocompleteValue] = useState<PersonSuggestion | null>(null);
  const [suggestions, setSuggestions] = useState<PersonSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [sizingDialogOpen, setSizingDialogOpen] = useState(false);
  const [stateActionPending, setStateActionPending] = useState(false);

  const liveRef = useRef<HTMLDivElement | null>(null);

  const performLookup = useCallback(
    async (badgeId: string) => {
      setErrorMessage(undefined);
      setState('loading');

      if (!hasPeopleData) {
        setState('error');
        setErrorMessage('No people data yet. Open Manage People and run sync.');
        return;
      }

      // Allow any input to reach the server for proper error handling
      if (!badgeId.trim()) {
        setState('error');
        setErrorMessage(
          `Please enter ${person.article} ${person.individualIdLabel}, SSN, or name.`,
        );
        return;
      }

      try {
        const customer = await customerLookupMutation.mutateAsync(badgeId);
        setCustomer(customer);
        setState('loaded');
        setIndividualId('');
        setSuggestions([]);
        if (liveRef.current) {
          liveRef.current.textContent = `Customer loaded: ${customer.display_name}`;
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setState('error');
        setErrorMessage(message);
        if (isCustomerEligibilityError(error)) {
          const reason = getEligibilityReason(error);
          if (reason) {
            setErrorMessage(`${message}. ${reason}`);
          }
        }
        setCustomer(null);
        if (liveRef.current) {
          liveRef.current.textContent = 'Customer lookup failed';
        }
      }
    },
    [setCustomer, customerLookupMutation, hasPeopleData],
  );

  useEffect(() => {
    const persistedIndividualId = getPersistedIndividualId();
    if (persistedIndividualId && !currentCustomer && hasPeopleData) {
      void performLookup(persistedIndividualId);
    }
    // We intentionally omit performLookup to avoid re-running the lookup while the mutation stabilizes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomer, getPersistedIndividualId, hasPeopleData]);

  // Service-member auto-load: when the session user has a linked person entity,
  // silently load that record without requiring a manual search.
  useEffect(() => {
    if (!isIdentitySelectionLocked || !myPersonEntityId || currentCustomer || !hasPeopleData)
      return;
    const loadOwnRecord = async () => {
      try {
        // For demo SM sessions: individual_id may be stored on the user object (new sessions)
        // or we fall back to looking up by email — avoids the PostgREST entity fetch which
        // 404s because the HS256 demo token routes to the actions API, not PostgREST.
        if (isDemoServiceMemberSession) {
          // Prefer individual_id stored at login (new sessions).
          // For older sessions, fetch it via the non-authed DB-direct endpoint
          // — avoids the PostgREST RLS restriction and the display_name comma issue.
          let lookupId = user?.individualId;
          if (!lookupId && user?.id) {
            try {
              const res = await httpClient.get<{ individual_id: string | null }>(
                `${API_CONFIG.ENDPOINTS.DEMO_SM_INDIVIDUAL_ID}/${user.id}`,
                { skipAuth: true },
              );
              lookupId = res.individual_id ?? undefined;
            } catch {
              // fall through
            }
          }
          if (lookupId) {
            await performLookup(lookupId);
          }
          return;
        }
        const entity = await httpClient.get<{ entity_data: { individual_id?: string } }>(
          `/api/rest/data/v1/entities/${myPersonEntityId}?select=entity_data`,
        );
        const individualId = entity?.entity_data?.individual_id;
        if (individualId) {
          await performLookup(individualId);
        }
      } catch {
        // Non-fatal: service member will see normal idle state
      }
    };
    void loadOwnRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdentitySelectionLocked, myPersonEntityId, currentCustomer, hasPeopleData]);

  // Sync state with current customer
  useEffect(() => {
    setState(currentCustomer ? 'loaded' : 'idle');
  }, [currentCustomer]);

  const handleScan = useCallback(
    async (scannedIndividualId: string) => {
      setErrorMessage(undefined);
      setState('loading');

      if (!hasPeopleData) {
        setState('error');
        setErrorMessage('No people data yet. Open Manage People and run sync.');
        return;
      }

      try {
        const customer = await badgeLookupMutation.mutateAsync(scannedIndividualId);
        setCustomer(customer);
        setState('loaded');
        if (liveRef.current) {
          liveRef.current.textContent = `Customer loaded: ${customer.display_name}`;
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setState('error');
        setErrorMessage(message);
        if (isCustomerEligibilityError(error)) {
          const reason = getEligibilityReason(error);
          if (reason) {
            setErrorMessage(`${message}. ${reason}`);
          }
        }
        setCustomer(null);
        if (liveRef.current) {
          liveRef.current.textContent = 'Customer lookup failed';
        }
      }
    },
    [badgeLookupMutation, hasPeopleData, setCustomer],
  );

  // Debounced suggestions fetch — uses EntityService (same as People Management screen)
  const fetchSuggestions = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setSuggestions([]);
      setSuggestionsError(undefined);
      setSuggestionsLoading(false);
      return;
    }
    const q = query.trim();
    const searchFields = /^\d+$/.test(q)
      ? [
          'display_name',
          'entity_data->>first_name',
          'entity_data->>last_name',
          'entity_data->>individual_id',
        ]
      : ['display_name', 'entity_data->>first_name', 'entity_data->>last_name'];
    const orFilter = buildPostgrestOrIlikeFilter(searchFields, q);
    debounceRef.current = setTimeout(async () => {
      setSuggestionsLoading(true);
      setSuggestionsError(undefined);
      try {
        const results = await entityService.list({
          type: 'person',
          limit: 10,
          order: 'display_name.asc',
          filters: { or: orFilter, status: 'eq.active' },
        });
        setSuggestions(
          results.map((e) => ({
            id: e.id,
            display_name: e.display_name,
            rank: e.entity_data.rank as string | undefined,
            unit: e.entity_data.unit as string | undefined,
            individual_id: e.entity_data.individual_id as string | undefined,
          })),
        );
      } catch (error) {
        const message = getErrorMessage(error);
        setSuggestions([]);
        setSuggestionsError(message);
        if (liveRef.current) {
          liveRef.current.textContent = 'Customer suggestions failed to load';
        }
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);
  }, []);

  const handleManualSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      performLookup(individualId.trim());
    }
  };

  const retry = () => {
    setState('idle');
    setErrorMessage(undefined);
  };

  const handleIndividualStateAction = useCallback(
    async (action: 'clear' | 'reactivate') => {
      if (!currentCustomer?.individual_id) {
        notifications.error(
          `Missing ${person.individualIdLabel}`,
          `Unable to update ${person.lower} state without ${person.article} ${person.individualIdLabel}.`,
        );
        return;
      }

      setStateActionPending(true);

      try {
        const stateResult =
          action === 'clear'
            ? await iemService.clearIndividual(currentCustomer.individual_id)
            : await iemService.reactivateIndividual(currentCustomer.individual_id);

        setCustomer({
          ...currentCustomer,
          cleared_status: stateResult.cleared_status,
          cleared_at: stateResult.cleared_at,
          cleared_by_name: stateResult.cleared_by_name,
        });

        notifications.success(
          action === 'clear' ? `${person.label} Cleared` : `${person.label} Reactivated`,
          action === 'clear'
            ? `Front Window workflows are now disabled for this ${person.lower}.`
            : `Front Window workflows are now enabled for this ${person.lower}.`,
        );
      } catch (error) {
        notifications.error(
          action === 'clear'
            ? `Unable to Clear ${person.label}`
            : `Unable to Reactivate ${person.label}`,
          getErrorMessage(error),
        );
      } finally {
        setStateActionPending(false);
      }
    },
    [currentCustomer, notifications, setCustomer],
  );

  return (
    <Card>
      {state === 'loading' && <LinearProgress role="status" aria-label="Looking up customer" />}
      <CardContent sx={{ pt: 2 }}>
        <Box sx={{ position: 'absolute', width: 1, height: 0, overflow: 'hidden' }}>
          <div ref={liveRef} aria-live="polite" />
        </Box>

        {!isIdentitySelectionLocked && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
            }}
          >
            <Stack spacing={2}>
              <>
                {getEnvironmentBanner() !== null && (
                  <>
                    <ScanBadgeButton
                      onScan={handleScan}
                      loading={state === 'loading'}
                      disabled={isScanBlocked}
                    />
                    <Divider>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: 1 }}
                      >
                        OR
                      </Typography>
                    </Divider>
                  </>
                )}
                <Box sx={{ mt: 0.5 }}>
                  <Autocomplete<PersonSuggestion, false, false, true>
                    freeSolo
                    options={suggestions}
                    loading={suggestionsLoading}
                    noOptionsText={suggestionsError ?? 'No matching people found'}
                    value={autocompleteValue}
                    inputValue={individualId}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.display_name
                    }
                    filterOptions={(x) => x}
                    onInputChange={(_e, value) => {
                      setIndividualId(value);
                      fetchSuggestions(value);
                    }}
                    onChange={(_e, option) => {
                      setAutocompleteValue(null);
                      if (option && typeof option !== 'string') {
                        setIndividualId(option.display_name);
                        void performLookup(option.individual_id ?? option.display_name);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.display_name}</Typography>
                          {option.unit && (
                            <Typography variant="caption" color="text.secondary">
                              {option.unit}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Person Search"
                        placeholder="ID | SSN | Name"
                        size="small"
                        error={Boolean(suggestionsError)}
                        helperText={suggestionsError}
                        onKeyDown={handleManualSubmit}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {suggestionsLoading && <CircularProgress color="inherit" size={16} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </>
            </Stack>
          </Paper>
        )}

        {!hasPeopleData && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <>
              {'No people data yet. '}
              <Link component={RouterLink} to="/person">
                Sync now
              </Link>
              .
            </>
          </Alert>
        )}

        {(state === 'loading' ||
          (isIdentitySelectionLocked && state === 'idle' && !currentCustomer)) && (
          <CustomerProfileSkeleton />
        )}

        {state === 'idle' && !currentCustomer && !isIdentitySelectionLocked && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {`Enter ${person.article} ${person.individualIdLabel}, SSN, or name, or scan a badge to load a customer.`}
          </Typography>
        )}

        {state === 'loaded' && currentCustomer && (
          <Box role="region" aria-label="Customer profile" sx={{ mt: 2 }}>
            <ProfileSummary
              person={currentCustomer}
              onManageSizing={
                canManageSizingForCurrentCustomer ? () => setSizingDialogOpen(true) : undefined
              }
              onClearIndividual={
                isIdentitySelectionLocked ? undefined : () => handleIndividualStateAction('clear')
              }
              onReactivateIndividual={
                isIdentitySelectionLocked
                  ? undefined
                  : () => handleIndividualStateAction('reactivate')
              }
              stateActionPending={stateActionPending}
            />
          </Box>
        )}

        {state === 'error' && (
          <Alert severity="error">
            {errorMessage}
            <Box sx={{ mt: 1 }}>
              <Button color="inherit" size="small" onClick={retry}>
                Retry
              </Button>
            </Box>
          </Alert>
        )}
      </CardContent>

      {/* Sizing Dialog */}
      {currentCustomer && (
        <PersonSizingDialog open={sizingDialogOpen} onClose={() => setSizingDialogOpen(false)} />
      )}
    </Card>
  );
};
